/* eslint-disable react-hooks/exhaustive-deps */

import { Box, Flex, Spinner, Stack, Text } from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import { debounce } from 'lodash';

import { fetchAPI } from '@/api/fetch';
import { apiUpdateMeetingNote } from '@/api/services/meeting.service';
import RichEditor from '@/components/forms/RichEditor';

interface InterviewNoteProps {
  meetingId: string;
  initialNote: string;
}

export default function InterviewNote({ meetingId, initialNote }: InterviewNoteProps) {
  const [note, setNote] = useState(initialNote);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const debouncedUpdateNotes = useCallback(
    debounce(async (noteText: string) => {
      setSaveStatus('saving');
      setErrorMessage(null);

      try {
        const response = await fetchAPI<void>({
          request: () => apiUpdateMeetingNote(meetingId, { note: noteText })
        });

        if (response.success) {
          setSaveStatus('saved');
          setTimeout(() => setSaveStatus('idle'), 2000);
        } else {
          setSaveStatus('error');
          setErrorMessage('Failed to update notes. Please try again.');
        }
      } catch (error) {
        console.error('Error updating meeting notes:', error);
        setSaveStatus('error');
        setErrorMessage('An unexpected error occurred. Please try again.');
      }
    }, 1000),
    []
  );

  useEffect(() => {
    debouncedUpdateNotes(note);

    return () => {
      debouncedUpdateNotes.cancel();
    };
  }, [note, debouncedUpdateNotes]);

  const handleNoteChange = (content: string) => {
    setNote(content);
  };

  return (
    <Box
      w="100%"
      h="100%"
      border="1px solid"
      borderColor="neutrals.200"
      borderRadius="12px"
      position="relative"
    >
      <Stack gap="0" h="100%">
        <Flex justifyContent="space-between" alignItems="center" p="16px 24px">
          <Text fontSize="18px" fontWeight="semibold" color="neutrals.700">
            Note
          </Text>
          {saveStatus === 'saving' && (
            <Flex alignItems="center" gap={2}>
              <Spinner size="sm" />
              <Text fontSize="14px" color="neutrals.500">
                Saving...
              </Text>
            </Flex>
          )}
          {saveStatus === 'saved' && (
            <Text fontSize="14px" color="success.700">
              Saved
            </Text>
          )}
        </Flex>
        <Box borderBottom="1px solid" borderColor="neutrals.200" />
        <Box p="16px 24px">
          <RichEditor
            key={meetingId}
            content={note}
            placeholder="Write your notes about this interview here..."
            onChange={handleNoteChange}
            type="interview-note"
          />
        </Box>
        {saveStatus === 'error' && errorMessage && (
          <Text fontSize="14px" color="error.700" p="8px 24px">
            {errorMessage}
          </Text>
        )}
      </Stack>
    </Box>
  );
}
